import { useState, useEffect } from 'react';

const BRANDING_API = '/api/v1/aif/branding';

const DEFAULT_BRANDING = {
  tenant_name: 'AI Farm',
  app_name: 'AI Farm',
  tagline: null,
  logo_url: null,
  primary_color: null,
  secondary_color: null,
  powered_by_visible: false,
};

/**
 * Fetches tenant branding based on hostname (unauthenticated) or token (authenticated).
 * Sprint 45: when token provided, also fetches /tenant/branding for richer data.
 * Applies primary/secondary colors as CSS custom properties on :root.
 */
export function useBranding(token) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);

  // Unauthenticated branding (hostname-based, for login page)
  useEffect(() => {
    let cancelled = false;

    async function fetchBranding() {
      try {
        const res = await fetch(BRANDING_API);
        if (!res.ok) throw new Error('No branding');
        const data = await res.json();
        if (!cancelled) {
          setBranding(prev => ({ ...prev, ...data }));
          applyColors(data.primary_color, data.secondary_color);
        }
      } catch {
        // No custom branding — use defaults
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    fetchBranding();
    return () => { cancelled = true; };
  }, []);

  // Authenticated branding (more complete, includes app_name + powered_by)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    fetch('/api/v1/aif/tenant/branding', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data) {
          setBranding(prev => ({ ...prev, ...data }));
          if (data.primary_color) applyColors(data.primary_color, data.secondary_color);
          if (data.app_name) document.title = data.app_name;
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [token]);

  return { branding, loaded };
}

function applyColors(primary, secondary) {
  const root = document.documentElement;
  if (primary) {
    root.style.setProperty('--aif-brand-primary', primary);
    // Generate lighter/darker variants from hex
    root.style.setProperty('--aif-brand-primary-light', lighten(primary, 0.85));
    root.style.setProperty('--aif-brand-primary-dark', darken(primary, 0.15));
  }
  if (secondary) {
    root.style.setProperty('--aif-brand-secondary', secondary);
  }
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function lighten(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

function darken(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const dr = Math.round(r * (1 - amount));
  const dg = Math.round(g * (1 - amount));
  const db = Math.round(b * (1 - amount));
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
}
